import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, UserMinus, UserPlus } from 'lucide-react';
import { useState } from 'react';
import type { TicketSummary } from '@aurora/contracts';
import { patch, type ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { useAgents } from '../../lib/directory';
import { useSession } from '../../lib/session';
import { Avatar, Badge, Spinner } from '../../components/ui';
import { useToast } from '../../components/toast';
import { invalidateTicket, patchTicketInLists } from './list-cache';

/**
 * Taking a ticket from the list without a menu — the `a` shortcut.
 *
 * Sent with the version the row was drawn from, as the dashboard's "Assign to
 * me" is: a ticket somebody else took a moment ago is refused, and the list
 * refreshed, rather than silently taken from them. `pending` holds the rows
 * on their way, so each can show it is busy.
 */
export function useAssignToMe() {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<ReadonlySet<number>>(() => new Set());
  const me = session?.user.id ?? null;

  const settle = (number: number) =>
    setPending((current) => {
      const next = new Set(current);
      next.delete(number);
      return next;
    });

  const assign = useMutation<{ data?: TicketSummary }, ApiError, TicketSummary>({
    mutationFn: (ticket) =>
      patch(`/tickets/${ticket.number}`, {
        assignee_id: me,
        ...(typeof ticket.version === 'number' ? { version: ticket.version } : {}),
      }),
    onMutate: (ticket) => setPending((current) => new Set(current).add(ticket.number)),
    onSuccess: (response, ticket) => {
      if (response?.data) patchTicketInLists(queryClient, response.data);
      invalidateTicket(queryClient, ticket.number);
      toast.success(`#${ticket.number} is assigned to you`);
    },
    onError: (error, ticket) => {
      toast.error(error);
      // Out of date — somebody else got there first. Show what is true now.
      if (error.code === 'stale_write') invalidateTicket(queryClient, ticket.number);
    },
    onSettled: (_data, _error, ticket) => settle(ticket.number),
  });

  const run = (ticket: TicketSummary) => {
    if (me === null || pending.has(ticket.number)) return;
    if (ticket.assignee?.id === me) {
      toast.info(`#${ticket.number} is already assigned to you`);
      return;
    }
    assign.mutate(ticket);
  };

  return { run, pending };
}

/**
 * Who is working a ticket, changeable from the list.
 *
 * Picking up unassigned work used to mean opening the ticket, finding the
 * assignee field and choosing yourself — three steps for the second most
 * common thing an agent does. From here it is one menu and one click, and it
 * goes through the ordinary update, so assignment rules, audit and
 * notifications behave exactly as they do inside the ticket.
 */
export function InlineAssignee({
  ticket,
  busy = false,
}: {
  ticket: TicketSummary;
  /** Being assigned from elsewhere — the row's `a` shortcut — so it shows as busy here too. */
  busy?: boolean;
}) {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const agents = useAgents(open);
  const me = session?.user.id ?? null;

  const assign = useMutation<{ data?: TicketSummary }, ApiError, { id: number | null; name: string | null }>({
    mutationFn: ({ id }) => patch(`/tickets/${ticket.number}`, { assignee_id: id }),
    onSuccess: (response, choice) => {
      if (response?.data) patchTicketInLists(queryClient, response.data);
      invalidateTicket(queryClient, ticket.number);
      toast.success(
        choice.id === null
          ? `#${ticket.number} is now unassigned`
          : choice.id === me
            ? `#${ticket.number} is assigned to you`
            : `#${ticket.number} is assigned to ${choice.name ?? 'them'}`,
      );
    },
    onError: (error) => toast.error(error),
  });

  const current = ticket.assignee;
  const working = assign.isPending || busy;
  const pick = (choice: { id: number | null; name: string | null }) => {
    if (!working) assign.mutate(choice);
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          // Busy rather than disabled, so keyboard focus stays where it was.
          aria-busy={working || undefined}
          aria-label={current ? `Assigned to ${current.name}. Change it.` : 'Unassigned. Assign it.'}
          className="group/assignee inline-flex max-w-full min-w-0 items-center gap-1.5 rounded py-0.5 text-start transition-colors hover:bg-sunken"
        >
          {working ? (
            <Spinner className="size-3" label="Assigning" />
          ) : current ? (
            <>
              <Avatar name={current.name} size="xs" />
              <span dir="auto" className="truncate text-mini text-ink-muted">
                {current.name}
              </span>
            </>
          ) : (
            // Unassigned is a state that needs acting on, not an absence.
            <Badge tone="caution">Unassigned</Badge>
          )}
          <ChevronDown
            aria-hidden
            className="size-3 shrink-0 text-ink-faint opacity-40 transition-opacity group-hover/assignee:opacity-100 group-focus-visible/assignee:opacity-100"
          />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="animate-pop scrollbar-slim z-50 max-h-80 min-w-56 overflow-y-auto rounded-card border border-line bg-raised p-1 shadow-float"
        >
          {me !== null && current?.id !== me && (
            <DropdownMenu.Item onSelect={() => pick({ id: me, name: session?.user.name ?? null })} className={ITEM}>
              <UserPlus aria-hidden className="size-3.5 text-accent" />
              Assign to me
            </DropdownMenu.Item>
          )}
          {current && (
            <DropdownMenu.Item onSelect={() => pick({ id: null, name: null })} className={ITEM}>
              <UserMinus aria-hidden className="size-3.5 text-ink-subtle" />
              Unassign
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Label className="px-2 py-1 text-mini font-medium text-ink-subtle">Agents</DropdownMenu.Label>
          {agents.isLoading && <p className="px-2 py-1.5 text-mini text-ink-subtle">Loading agents…</p>}
          {agents.isError && (
            <p role="alert" className="px-2 py-1.5 text-mini text-critical-ink">
              Couldn’t load agents.
            </p>
          )}
          {(agents.data ?? []).map((agent) => {
            const selected = current?.id === agent.id;
            return (
              <DropdownMenu.Item
                key={agent.id}
                disabled={selected}
                onSelect={() => pick({ id: agent.id, name: agent.name })}
                className={cn(ITEM, selected && 'text-ink-faint')}
              >
                <Avatar name={agent.name} size="xs" />
                <span dir="auto" className="min-w-0 flex-1 truncate">
                  {agent.name}
                </span>
                {/* No open-ticket count: the directory's is a cache refreshed
                    every ten minutes, and a wrong number is worse than none. */}
                {agent.is_available === false && <Badge>Away</Badge>}
                {selected && <Check aria-hidden className="size-3.5 text-accent" />}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const ITEM =
  'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body text-ink outline-none data-highlighted:bg-sunken data-disabled:cursor-default';
